import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import usePageMetadata from '../utils/usePageMetadata';
import './Contact.css';

const CONNECT_PAY_URL = 'https://www.connectpay.club/profile/689e1a02cc3d1f003bab55a7';

const Contact = () => {
  const { user, token } = useAuth();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  usePageMetadata({
    title: 'Contact Support | The Ad Shop',
    description:
      'Need help with The Ad Shop? Signed-in customers can reach our support team directly through the secure contact form.',
    canonicalUrl: 'https://theadshop.co/contact',
    meta: [
      { property: 'og:title', content: 'Contact Support | The Ad Shop' },
      {
        property: 'og:description',
        content:
          'Reach our support team for product help. All other inquiries should be directed to our Connect Pay profile.',
      },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: 'https://theadshop.co/contact' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Contact Support | The Ad Shop' },
      {
        name: 'twitter:description',
        content:
          'Reach our support team for product help. All other inquiries should be directed to our Connect Pay profile.',
      },
    ],
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'Contact The Ad Shop Support',
      url: 'https://theadshop.co/contact',
      description:
        'Contact The Ad Shop support team for product help and assistance with your analytics dashboard.',
      mainEntity: {
        '@type': 'Organization',
        name: 'The Media Shop, LLC',
        url: 'https://theadshop.co/',
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer support',
          email: 'support@theadshop.co',
          availableLanguage: ['English'],
          areaServed: 'US',
        },
      },
      isPartOf: {
        '@type': 'WebSite',
        name: 'The Ad Shop',
        url: 'https://theadshop.co/',
      },
    },
  });

  const resetForm = () => {
    setSubject('');
    setMessage('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!subject.trim() || !message.trim()) {
      setError('Please include both a subject and a message before submitting.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      await axios.post(
        '/api/support/contact',
        { subject: subject.trim(), message: message.trim() },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSuccess('Thanks! Your support request has been sent. We typically respond within one business day.');
      resetForm();
    } catch (err) {
      console.error('Support message error:', err);
      setError(
        err.response?.data?.message ||
          'We were unable to send your message. Please try again or email support@theadshop.co.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-page">
      <div className="contact-container">
        <header className="contact-header">
          <h1>Contact Support</h1>
          <p className="contact-effective-date">Support requests are monitored Monday through Friday.</p>
        </header>

        <p className="support-notice">
          This form is reserved strictly for existing customer support inquiries. Messages about partnerships,
          sales pitches, or unrelated topics are marked as spam and the sender will be blocked. For all
          non-support inquiries, please use our Connect Pay profile:{' '}
          <a href={CONNECT_PAY_URL} target="_blank" rel="noopener noreferrer">
            connectpay.club/profile/689e1a02cc3d1f003bab55a7
          </a>
          .
        </p>
        <p className="support-notice enterprise-note">
          Requesting Enterprise pricing? Create a free Ad Shop account first so we can review your footprint,
          then send us a message here with the details.
        </p>

        {!user ? (
          <div className="contact-auth-required">
            <h2>Sign In Required</h2>
            <p>
              You must be signed in to submit a support ticket. Please{' '}
              <Link to="/login" className="contact-link">
                log in
              </Link>{' '}
              or{' '}
              <Link to="/signup" className="contact-link">
                create an account
              </Link>{' '}
              before contacting support.
            </p>
          </div>
        ) : (
          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="contact-field">
              <label htmlFor="subject">Subject</label>
              <input
                id="subject"
                type="text"
                name="subject"
                placeholder="Briefly describe your issue"
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                maxLength={150}
                disabled={loading}
                required
              />
            </div>

            <div className="contact-field">
              <label htmlFor="message">How can we help?</label>
              <textarea
                id="message"
                name="message"
                placeholder="Share the details we need to troubleshoot quickly. Include relevant site URLs, timeframes, or screenshots."
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                rows={8}
                maxLength={4000}
                disabled={loading}
                required
              />
            </div>

            {error && <div className="contact-feedback error">{error}</div>}
            {success && <div className="contact-feedback success">{success}</div>}

            <div className="contact-actions">
              <button type="submit" className="contact-submit" disabled={loading}>
                {loading ? 'Sending…' : 'Send to Support'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default Contact;

